import { KeyboardEvent, MouseEvent, ReactNode, forwardRef } from 'react'
import classNames from 'classnames/bind'

import { SurfaceType } from '../plain-dropdown-surface'
import styles from './plain-button.module.scss'

const cx = classNames.bind(styles)

export type PlainButtonProps = {
  children: ReactNode
  type?: 'button' | 'submit' // 'reset' type forbidden, do not use
  id?: string
  name?: string
  disabled?: boolean
  outline?: boolean
  className?: string
  danger?: boolean
  success?: boolean
  warning?: boolean
  autoFocus?: boolean
  compact?: boolean
  form?: string
  onClick?: (e: MouseEvent<HTMLButtonElement>) => void | Promise<void>
  onKeyUp?: (e: KeyboardEvent<HTMLButtonElement>) => void | Promise<void>
  ariaLabel?: string
  ariaHaspopup?: SurfaceType
  ariaExpanded?: 'true' | 'false'
}

/**
 * Компонент для отображения кнопок с различными стилями и функциональностью.
 *
 * Поддерживает различные типы кнопок (button, submit),
 * может быть настроена на режим опасности, успеха, предупреждения,
 * а также имеет опции для отключения, выделения контура,
 * автофокусировки, компактного размера и формы.
 *
 * @param {object} props
 * @param {React.ReactNode} props.children - Содержимое кнопки.
 * @param {'button'|'submit'} props.type - Тип кнопки (по умолчанию "button").
 * @param {string} props.id - Идентификатор кнопки.
 * @param {string} props.name - Имя кнопки.
 * @param {boolean} props.disabled - Определяет, является ли кнопка отключенной.
 * @param {boolean} props.outline - Определяет, является ли кнопка контурной.
 * @param {string} props.className - Дополнительные классы для кнопки.
 * @param {boolean} props.danger - Определяет, является ли кнопка опасной.
 * @param {boolean} props.success - Определяет, является ли кнопка успешной.
 * @param {boolean} props.warning - Определяет, является ли кнопка предупреждающей.
 * @param {boolean} props.autoFocus - Определяет, должна ли кнопка автоматически фокусироваться.
 * @param {boolean} props.compact - Определяет, должна ли кнопка быть компактной.
 * @param {string} props.form - Имя формы, к которой привязана кнопка.
 * @param {function} props.onClick - Обработчик клика на кнопку.
 * @param {function} props.onKeyUp - Обработчик события `keyup` для кнопки.
 * @param {string} props.ariaLabel - Текст для чтения с помощью экранного ридера.
 * @param {string} props.ariaHaspopup - Указывает, имеет ли кнопка связанное всплывающее меню.
 * @param {'true'|'false'} props.ariaExpanded - Указывает, является ли связанное всплывающее меню открытым.
 * @returns {JSX.Element} JSX элемент, представляющий кнопку.
 */
const PlainButton = forwardRef<HTMLButtonElement, PlainButtonProps>(
  (
    {
      children,
      type,
      id,
      name,
      disabled,
      outline,
      className,
      danger,
      success,
      warning,
      autoFocus,
      compact,
      form,
      onClick,
      onKeyUp,
      ariaLabel,
      ariaHaspopup,
      ariaExpanded,
    }: PlainButtonProps,
    ref,
  ) => {
    const props = {
      id,
      name,
      'aria-label': ariaLabel,
      'aria-haspopup': ariaHaspopup,
      'aria-expanded': ariaExpanded,
      ref,
      onClick,
      onKeyUp,
      disabled,
      autoFocus,
      className: cx(
        styles.root,
        {
          default: !outline && !disabled,
          outline,
          disabled,
          danger,
          success,
          warning,
          compact,
        },
        className,
      ),
      form,
    }

    // type must be static:
    if (type === 'submit') {
      return (
        <button type="submit" {...props}>
          <span className={styles.label}>{children}</span>
        </button>
      )
    }

    // default type: "button"
    return (
      <button type="button" {...props}>
        <span className={styles.label}>{children}</span>
      </button>
    )
  },
)

export default PlainButton
